// ============================================================
// ANSICHTSSACHE-AGENT – Die ganze Zeile oeffnet, auch beim Wischen
// ============================================================
// In jeder Liste oeffnet die ganze Zeile (`zeilenverweis.tsx`): ein
// echter Verweis traegt eine unsichtbare Flaeche ueber die Zeile.
// Auf dem Telefon bleibt beim seitlichen Wischen der Titel stehen
// (`styles/globals.css`, Listentabelle). Eine klebende Zelle liegt
// ueber der Flaeche — und sitzt der Verweis in ihr, ist sie sogar
// ihr Bezugskasten, die Flaeche reicht dann nur noch ueber die eine
// Zelle.
//
// Fuer den Rest der Zeile springt dieser Helfer ein: ein Tipp in
// eine Tabellenzeile, der weder einen Verweis noch einen Knopf noch
// ein Feld trifft, loest den Verweis der Zeile aus. EIN Bauteil fuer
// alle Listen; keine Liste muss etwas dafuer tun. Auf breiten
// Schirmen schweigt er — dort liegt die Flaeche ueber der ganzen
// Zeile, und jeder Klick trifft ohnehin den Verweis.
//
// WARUM DER HORCHER AN JEDER TABELLE HAENGT und nicht einmal am
// Dokument: Safari auf dem iPhone meldet einen Tipp auf etwas, das
// nicht anklickbar ist, nur dann als Klick, wenn das Element oder
// einer seiner Vorfahren selbst einen Klick-Horcher traegt —
// Dokument und `body` zaehlen nicht. Am Dokument kam deshalb nichts
// an (gemessen am 10.10.2026 in WebKit). Neue Tabellen (Seitenwechsel,
// Auffrischen) findet ein `MutationObserver`; jede wird einmal
// versehen.
//
// WELCHE SCHON VERSEHEN SIND, STEHT IM SPEICHER, NICHT IM DOM. Bis
// zum 22.10.2026 trug jede Tabelle dafuer `data-zeilen-tippen`. Der
// Helfer haengt aber im Wurzel-Layout und laeuft, bevor der
// Seiteninhalt hydriert ist (der kommt hinter Suspense spaeter) —
// er schrieb das Attribut also in Markup, das React noch gar nicht
// uebernommen hatte, und React meldete beim Hydrieren „attributes
// of the server rendered HTML didn't match“ (Zeiterfassung, Projekt
// › Zeit). Ein Horcher aendert am Markup nichts; die Merkliste
// (`WeakSet`) vergisst eine Tabelle, sobald React sie ersetzt.
// ============================================================

"use client";

import { useEffect } from "react";

const SCHMAL = "(width < 48rem)";

/** Was in der Zeile selbst etwas tut — dort bleibt der Tipp, wo er ist. */
const EIGENSTAENDIG =
  'a, button, input, select, textarea, label, summary, [role="button"], [role="checkbox"], [role="menuitem"], [contenteditable="true"]';

function tippen(e: MouseEvent) {
  if (!window.matchMedia(SCHMAL).matches || e.defaultPrevented || e.button !== 0) return;
  const ziel = e.target instanceof Element ? e.target : null;
  // Tabellenzeile oder Rasterzeile einer Kartenliste (`karten.tsx`).
  const zeile = ziel?.closest(
    ".listentabelle > tbody > tr, [data-kartenstapel] [style*='grid-template-columns']",
  );
  if (!ziel || !zeile || ziel.closest(EIGENSTAENDIG)) return;
  zeile.querySelector<HTMLElement>('[class*="after:inset-0"]')?.click();
}

/** Die Tabellen und Kartenstapel, die den Horcher schon tragen. */
const versehene = new WeakSet<Element>();

function versehen() {
  for (const tabelle of document.querySelectorAll<HTMLElement>(
    "table.listentabelle, [data-kartenstapel]",
  )) {
    if (versehene.has(tabelle)) continue;
    versehene.add(tabelle);
    tabelle.addEventListener("click", tippen);
  }
}

export function ZeilenTippen() {
  useEffect(() => {
    versehen();
    const beobachter = new MutationObserver(versehen);
    beobachter.observe(document.body, { childList: true, subtree: true });
    return () => beobachter.disconnect();
  }, []);

  return null;
}
